"use client"
import { useEffect, useState } from "react"
import sdk from "@farcaster/frame-sdk"
import { useAccount } from "wagmi"
import { useRouter } from "next/navigation"

import Cookies from "js-cookie"
import { isInFrame } from "@/app/util/farcaster/frames"
import { usePrivy, useUser } from "@privy-io/react-auth"
import { useLoginToFrame } from "@privy-io/react-auth/farcaster"
import { createUser } from "@/lib/api/users"

/**
 * This is a component that is used on all Farcaster frame pages.
 *
 * It indicates to the frame when it's "ready". If you don't include this, the frame will not load
 * since it never receives the 'ready' event.
 *
 * Note that this cannot be a hook, since we render this in React server components.
 *
 * This should really be used alongside the `metaTags.tsx` frame tags.
 */
export default function FarcasterClientTags() {
  const [isSDKLoaded, setIsSDKLoaded] = useState(false)
  const account = useAccount()
  const router = useRouter()
  const { ready, authenticated, user, logout } = usePrivy()
  const { initLoginToFrame, loginToFrame } = useLoginToFrame()
  const { refreshUser } = useUser()

  /**
   * Very aggressively try to 'ready' the SDK.
   *
   * There are some possible race conditions (?) where the SDK is not ready
   * when we need it to be, which prevents the Farcaster frame from finishing loading.
   */
  useEffect(() => {
    sdk.actions.ready()
    console.log("SDK ready")
  }, [])

  useEffect(() => {
    const load = async () => {
      await sdk.actions.ready()

      const context = await sdk.context
      const inFrame = await isInFrame()

      if (!context) {
        console.log("No Farcaster context: user not viewing in frame?")

        // If we have the inFrame cookie set, remove it, since we are not in frame anymore.
        if (inFrame) {
          // Remove the cookie, so it doesn't carry over to normal browsing sessions.
          Cookies.remove("frame")
          router.refresh()
        }
        return
      }

      setIsSDKLoaded(true)

      console.log("SDK context", context)
    }
    if (sdk && !isSDKLoaded) {
      console.log("SDK loaded")
      load()
    }
  }, [isSDKLoaded])

  // Login to Frame with Privy automatically
  useEffect(() => {
    // Wait for SDK and address to be available
    if (!isSDKLoaded || !account?.address) {
      console.warn(
        "SDK not loaded or account address not set: user may be viewing this on Farcaster web",
        account?.address,
        isSDKLoaded
      )
      return
    }

    // Wait for Privy to be ready and user to be logged out
    if (ready && !authenticated) {
      const login = async () => {
        console.log(
          "SDK loaded, address available, user logged out, and Privy ready... logging-in..."
        )

        const { nonce } = await initLoginToFrame()
        const result = await sdk.actions.signIn({ nonce: nonce })
        const loggedInUser = await loginToFrame({
          message: result.message,
          signature: result.signature,
        })

        if (!loggedInUser) {
          console.error("Unable to login to Farcaster frame")
          return
        }

        console.log("Logged in user", loggedInUser)

        // Create user record linked to Privy ID, add userId to Privy user custom metadata
        if (!loggedInUser.customMetadata) {
          console.log("New user created! Creating DB record", loggedInUser)

          try {
            // Create user record linked to Privy ID, add userId to Privy user custom metadata
            await createUser()

            // Refresh privy-id-token to contain updated custom metadata
            await refreshUser()

            console.log("Created DB record")
          } catch (e) {
            logout()
          }
        }

        // Refresh the page to update logged-in state
        await router.refresh()

        // Prompt user to add the frame (if the frame is already added, this will do nothing.)
        // If the user installs the frame, Farcaster notifications will be enabled for them in the webhook.
        await sdk.actions.addFrame()
      }
      login()
    }
  }, [ready, authenticated, isSDKLoaded, account?.address])

  return null
}
